import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, pagedOrThrow, pageQuery, type Envelope, type PageRequest, type PagedData } from "./client";
import type { Host, IFilter, Player, PlayerNote, Role, Session, Site } from "./models";
import { environment } from "../env";
import { readClaims } from "../auth/token";

// SAME CADENCE AS THE ANGULAR IN PLAY PAGE'S `setInterval(..., 15000)`
export const IN_PLAY_POLL_MS = 15_000;

// EVERY KEY IS PREFIXED WITH THE SIGNED-IN USER, SO ONE USER'S CACHED PLAYERS
// CAN NEVER ANSWER ANOTHER USER'S QUERY ON A SHARED DEVICE. THE CACHE IS ALSO
// CLEARED OUTRIGHT ON LOGIN, LOGOUT AND A `?t=` HANDOFF FOR A DIFFERENT USER.
function userKey(): string {
  return String(readClaims()?.userId ?? "anonymous");
}

export const queryKeys = {
  roles: () => [userKey(), "roles"] as const,
  hosts: () => [userKey(), "hosts"] as const,
  sites: () => [userKey(), "sites"] as const,
  sessions: (siteId: number, filters: IFilter[], page: PageRequest) =>
    [userKey(), "sessions", siteId, filters, page] as const,
  player: (lookup: PlayerLookup) => [userKey(), "player", lookup] as const,
  notes: (cmsPlayerId: string) => [userKey(), "notes", cmsPlayerId] as const,
};

// AUTH ------------------------------------------------------------------------

export function authenticate(username: string, password: string) {
  return apiRequest<Envelope<{ token?: string }>>("/api/authenticate", {
    method: "POST",
    anonymous: true,
    body: { username, password, deviceId: "", application: environment.application },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: queryKeys.roles(),
    queryFn: ({ signal }) => apiRequest<Envelope<Role[] | null>>("/ht/roles", { signal }).then((r) => r.data ?? []),
    staleTime: Infinity,
  });
}

export function useHosts() {
  return useQuery({
    queryKey: queryKeys.hosts(),
    queryFn: ({ signal }) =>
      apiRequest<Envelope<Host[] | null>>("/ht/hosts", { signal }).then((r) => (r.data ?? []).filter((h) => h.display === true)),
    staleTime: Infinity,
  });
}

// IN PLAY ---------------------------------------------------------------------

export function useSites() {
  return useQuery({
    queryKey: queryKeys.sites(),
    queryFn: async ({ signal }) => {
      const r = await apiRequest<Envelope<Array<{ id: number; name: string; is_primary: boolean }>>>("/site/all-sites", { signal });
      return (r.data ?? []).map<Site>((s) => ({ id: s.id, name: s.name, isPrimary: s.is_primary }));
    },
    staleTime: Infinity,
  });
}

// PORT OF `SiteSelectorComponent.selectDefaultSite`
export function defaultSiteId(sites: Site[]): number | null {
  const s = sites.find((x) => x.id === 1) ?? sites.find((x) => x.isPrimary) ?? sites[0];
  return s?.id ?? null;
}

export function usePagedSessions(siteId: number | null, filters: IFilter[] | null, page: PageRequest) {
  return useQuery({
    queryKey: queryKeys.sessions(siteId ?? -1, filters ?? [], page),
    queryFn: ({ signal }) =>
      apiRequest<Envelope<PagedData<Session>>>(
        `/ht/paged-sessions?${pageQuery(page, { site_ids: String(siteId) })}`,
        { method: "POST", body: filters, signal },
      ).then((r) => pagedOrThrow(r.data, "paged-sessions")),
    enabled: siteId !== null && filters !== null,
    // POLLING. `refetchIntervalInBackground` STAYS FALSE, SO A BACKGROUNDED NATIVE
    // SHELL OR HIDDEN TAB STOPS POLLING AND RESUMES (WITH AN IMMEDIATE REFETCH) ON RETURN.
    refetchInterval: IN_PLAY_POLL_MS,
    // KEEP THE CURRENT PAGE ON SCREEN WHILE THE NEXT PAGE/SORT LOADS
    placeholderData: (previous) => previous,
  });
}

// PLAYER PROFILE --------------------------------------------------------------

// THE ROUTE PARAM IS A CARD ID, WHICH CAN BE SHARED. WHEN THE NAVIGATION KNOWS
// WHICH PLAYER IT MEANT IT PASSES `cmsPlayerId`, AND THE LOOKUP GOES THROUGH
// `/player/by-cms-id/` - SAME RULE AS THE ANGULAR PROFILE (ISSUE #78).
export type PlayerLookup = { by: "card"; cardId: string } | { by: "cms"; cmsPlayerId: string };

export function usePlayer(lookup: PlayerLookup) {
  return useQuery({
    queryKey: queryKeys.player(lookup),
    queryFn: async ({ signal }) => {
      const path =
        lookup.by === "cms"
          ? `/player/by-cms-id/${encodeURIComponent(lookup.cmsPlayerId)}`
          : `/player/${encodeURIComponent(lookup.cardId)}`;
      const r = await apiRequest<Envelope<Player | null>>(path, { signal });
      // A 200 CARRYING NO PLAYER IS A FAILED LOAD, NOT AN EMPTY ONE
      if (!r.data) throw new Error("The player could not be loaded.");
      if (lookup.by === "cms" && r.data.cmsPlayerId && r.data.cmsPlayerId !== lookup.cmsPlayerId) {
        throw new Error("The API returned a different player than the one requested.");
      }
      return r.data;
    },
  });
}

// `paged-player-notes` PAGES OF 5, AS THE ANGULAR NOTES SIDEBAR ASKS FOR
const NOTES_PAGE_SIZE = 5;

export function usePlayerNotes(cmsPlayerId: string | undefined, page: number) {
  return useQuery({
    queryKey: [...queryKeys.notes(cmsPlayerId ?? ""), page],
    queryFn: ({ signal }) => {
      const filters: IFilter[] = [
        { field: "cms_player_id", value: cmsPlayerId ?? "", valueDataType: "string", type: "=", condition: "AND" },
      ];
      return apiRequest<Envelope<PagedData<PlayerNote>>>(
        `/player/paged-player-notes?${pageQuery({ page, pageSize: NOTES_PAGE_SIZE, orderBy: "created_at", orderDir: "desc" })}`,
        { method: "POST", body: filters, signal },
      ).then((r) => pagedOrThrow(r.data, "paged-player-notes"));
    },
    // NEVER QUERY NOTES KEYED ON AN EMPTY ID - IT WOULD RETURN SOMEONE ELSE'S OR NOTHING
    enabled: !!cmsPlayerId,
    placeholderData: (previous) => previous,
  });
}

export function useCreateNote(cmsPlayerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => {
      if (!cmsPlayerId) throw new Error("This player has no CMS player id, so a note cannot be saved.");
      return apiRequest<unknown>("/player/create-player-note", {
        method: "POST",
        body: { cms_player_id: cmsPlayerId, message },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notes(cmsPlayerId ?? "") }),
  });
}

// PLAYERS ---------------------------------------------------------------------

// ONE ROW OF `/ht/paged-players`. `playerId` (OR `player_id`) IS THE CARD ID THE
// PROFILE ROUTE TAKES; `hostId === 0` IS HOW THE API MARKS AN UNHOSTED PLAYER.
export interface PlayerRow {
  playerId?: string;
  player_id?: string;
  cmsPlayerId?: string;
  name: string;
  hostId?: number;
  host?: { hostName?: string };
}

export function usePagedPlayers(filters: IFilter[], page: PageRequest) {
  return useQuery({
    queryKey: [userKey(), "players", filters, page] as const,
    queryFn: ({ signal }) =>
      apiRequest<Envelope<PagedData<PlayerRow>>>(`/ht/paged-players?${pageQuery(page)}`, {
        method: "POST",
        // A BARE FILTER ARRAY, `[]` WHEN NOTHING IS FILTERED
        body: filters,
        signal,
      }).then((r) => pagedOrThrow(r.data, "paged-players")),
    placeholderData: (previous) => previous,
  });
}

// TOP BAR -----------------------------------------------------------------------

// THE TOP BAR'S UNREAD BADGE: `POST /user/app-notifications` WITH THIS APP'S CODE,
// COUNTING `isNew`. SAME 5S CADENCE AS THE ANGULAR TOPBAR'S `setInterval`, BUT IT
// PAUSES WHILE THE APP IS IN THE BACKGROUND.
export function useNewNotificationCount() {
  return useQuery({
    queryKey: [userKey(), "app-notifications"],
    queryFn: ({ signal }) =>
      apiRequest<Envelope<Array<{ isNew?: boolean }> | null>>("/user/app-notifications", {
        method: "POST",
        body: { appCode: environment.application },
        signal,
      }).then((r) => r.data ?? []),
    refetchInterval: 5_000,
    select: (rows) => rows.filter((n) => n.isNew).length,
  });
}
